// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.

/** What playback resolves for a READY video: the stream URL plus its user-facing text. */
export interface VideoPlayback {
  url: string;
  title?: string;
  description?: string;
}

export type VideoAssetReferenceContentState = 'draft' | 'published';

export interface VideoAssetReference {
  modelType: string;
  modelId: number;
  contentBlockId: string;
  contentState: VideoAssetReferenceContentState;
  fundOpportunityExternalId?: string;
  fundOpportunityName?: string;
}

export type VideoAssetStatus = 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED';

/**
 * A video's editable metadata. `title` is the admin-facing library title (the
 * backend defaults it to `publicTitle`); `publicTitle` and `publicDescription`
 * are what viewers see over the video when it plays, and are mandatory.
 */
export interface VideoMetadata {
  title?: string;
  publicTitle: string;
  publicDescription: string;
}

export interface VideoAsset {
  externalId: string;
  /** Admin-facing library title. */
  title: string;
  /** Shown to viewers over the video when it plays. */
  publicTitle: string;
  publicDescription: string;
  /** Read-only admin info. */
  createdByName?: string;
  createdAt?: string;
  updatedAt?: string;
  fileSizeBytes?: number;
  status: VideoAssetStatus;
  ownerFundOpportunityId: number;
  ownerFundOpportunityExternalId?: string;
  originalFilename?: string;
  mimeType?: string;
  durationSeconds?: number;
  posterUrl?: string;
  errorMessage?: string;
  referenceCount?: number;
  references?: VideoAssetReference[];
}

/**
 * Video library: admins upload once, compression happens server-side, and the
 * same video can be linked into any fund opportunity's content. Upload is
 * presigned (same shape as uploadAssetContent) so the file goes straight to
 * GCS — this service never holds the raw video in the browser's fetch layer.
 */
export const adminBackendServiceVideoLibraryMixin = {
  requestVideoUpload(
    metadata: VideoMetadata,
    fundOpportunityId: string,
    fileName: string,
    contentType: string,
  ): Promise<VideoAsset & { uploadUrl: string }> {
    return this.authApiMutateAuthed(
      'contentManagement/videos',
      'POST',
      { ...metadata, fundOpportunityId, fileName, contentType },
      (json: any) => json.data,
    );
  },

  /** Edits any of the admin title / user-facing title / user-facing description. */
  updateVideoMetadata(externalId: string, fields: Partial<VideoMetadata>): Promise<VideoAsset> {
    return this.authApiMutateAuthed(
      `contentManagement/videos/${externalId}`,
      'PATCH',
      fields,
      (json: any) => json.data,
    );
  },

  completeVideoUpload(externalId: string): Promise<VideoAsset> {
    return this.authApiMutateAuthed(
      `contentManagement/videos/${externalId}/complete`,
      'POST',
      undefined,
      (json: any) => json.data,
    );
  },

  /** Full flow: request a signed URL, PUT the file to GCS, then confirm. */
  async uploadVideoToLibrary(
    metadata: VideoMetadata,
    fundOpportunityId: string,
    file: File,
    onUploadProgress?: (progress: number) => void,
  ): Promise<VideoAsset> {
    const contentType = file.type || 'video/mp4';
    const { externalId, uploadUrl } = await this.requestVideoUpload(
      metadata,
      fundOpportunityId,
      file.name,
      contentType,
    );
    await this.putFileViaXhr(uploadUrl, file, contentType, onUploadProgress);
    return this.completeVideoUpload(externalId);
  },

  /**
   * The stable playback URL for a READY video (GET /videos/:externalId,
   * checkLoginAnyRole on the backend — outside the contentManagement/
   * prefix since investors call this too, not just admins). Cacheable by
   * Cloud CDN once provisioned; falls back to a short-lived GCS signed URL
   * otherwise — same call either way, the backend decides.
   */
  getVideoPlaybackUrl(externalId: string): Promise<VideoPlayback> {
    return this.authApiGetAuthed(
      `videos/${externalId}?returnUrl=true`,
      'Unable to load video playback URL',
      (json: any) => ({ url: json.url, title: json.title, description: json.description }),
    );
  },

  listVideoLibrary(
    params: { title?: string; fundOpportunityId?: string } = {},
  ): Promise<VideoAsset[]> {
    const query = new URLSearchParams();
    if (params.title) query.set('title', params.title);
    if (params.fundOpportunityId) query.set('fundOpportunityId', params.fundOpportunityId);
    const queryString = query.toString();
    return this.authApiGetAuthed(
      `contentManagement/videos${queryString ? `?${queryString}` : ''}`,
      'Unable to load the video library',
      (json: any) => json.data ?? [],
    );
  },

  getVideoAsset(externalId: string): Promise<VideoAsset> {
    return this.authApiGetAuthed(
      `contentManagement/videos/${externalId}`,
      'Unable to load video',
      (json: any) => json.data,
    );
  },

  // Reference tracking (video_asset_references) is reconciled automatically
  // from content draft-save/publish on the backend — see
  // VideoReferenceReconciler — not mutated from here. Picking or removing a
  // library video on a card just updates the content (videoSource/
  // libraryVideoExternalId) like any other field; the next autosave is what
  // records or clears the reference, with no separate API call needed.
  deleteVideoAsset(externalId: string, force: boolean = false): Promise<{ deleted: boolean }> {
    return this.authApiMutateAuthed(
      `contentManagement/videos/${externalId}${force ? '?force=true' : ''}`,
      'DELETE',
      undefined,
      (json: any) => json.data,
    );
  },
};
